import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { HISTORY_OUTCOME_LABELS, HistoryDecision, PatientAppHistoryItem, PatientAppHistoryProposal, undecidedHistoryItems } from '../../../../core/models/patient-app-history';
import { historyItemLines, reviewSummary } from '../../../../core/models/inbox-review';
import { describeReferralFailure } from '../../../../core/models/referral.model';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { PatientAppService } from '../../../../core/services/patient-app.service';

export interface PatientAppHistoryDialogData {
  proposalId: string;
  groupId: string;
  patientName: string | null;
}

/** An icon for each of the patient's lists. */
const LIST_ICONS: Record<string, string> = {
  allergies: 'warning_amber', medicalHistory: 'healing', medicationHistory: 'medication', medicalTeam: 'medical_services',
};

interface HistoryListView {
  list: string;
  label: string;
  icon: string;
  items: (PatientAppHistoryItem & { main: string; secondary: string })[];
}

/**
 * Allergies, medical history, medications and doctors the patient added in the patient app: each item is filed into
 * the patient's list (marked "Patient reported") or left out. An item the list already holds needs no decision and is
 * not filed twice. The patient is told what became of each item.
 */
@Component({
  selector: 'app-patient-app-history-dialog',
  templateUrl: './patient-app-history-dialog.component.html',
  styleUrls: ['../inbox-review.scss'],
})
export class PatientAppHistoryDialogComponent extends BaseComponent implements OnInit {
  proposal: PatientAppHistoryProposal | null = null;
  /** Each list with its icon and every item split into a main and a secondary line, built once when it loads. */
  lists: HistoryListView[] = [];
  decisions: Record<string, HistoryDecision | undefined> = {};
  isLoading = true;
  loadError: string | null = null;
  isSaving = false;
  actionError: string | null = null;
  submitAttempted = false;

  readonly outcomeLabels = HISTORY_OUTCOME_LABELS;

  constructor(
    private patientAppService: PatientAppService,
    private dialogRef: MatDialogRef<PatientAppHistoryDialogComponent, { changed?: boolean }>,
    @Inject(MAT_DIALOG_DATA) public data: PatientAppHistoryDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.patientAppService.getHistoryProposal(this.data.proposalId, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (proposal) => {
        this.proposal = proposal;
        this.lists = proposal.lists.map((l) => ({
          list: l.list, label: l.label, icon: LIST_ICONS[l.list] || 'list',
          items: l.items.map((i) => ({ ...i, ...historyItemLines(l.list, i.item, i.text) })),
        }));
        this.isLoading = false;
      },
      error: (error) => { this.isLoading = false; this.loadError = describeReferralFailure(error, 'These items could not be loaded.'); },
    });
  }

  get undecided(): string[] {
    return this.proposal ? undecidedHistoryItems(this.proposal, this.decisions) : [];
  }

  /** "5 items · 2 decided", with how many the record already holds. */
  get summary(): string {
    if (!this.proposal) return '';
    const items = this.proposal.lists.flatMap((l) => l.items);
    const toDecide = items.filter((i) => !i.alreadyRecorded).length;
    return reviewSummary(toDecide, toDecide - this.undecided.length, items.length - toDecide, 'item', 'already in the record');
  }

  needsDecision(i: PatientAppHistoryItem): boolean {
    return !i.alreadyRecorded && !this.decisions[i.itemId];
  }

  setAll(decision: HistoryDecision): void {
    if (!this.proposal) return;
    for (const l of this.proposal.lists) for (const i of l.items) if (!i.alreadyRecorded) this.decisions[i.itemId] = decision;
  }

  save(): void {
    this.submitAttempted = true;
    if (!this.proposal || this.undecided.length || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    const decisions = Object.fromEntries(Object.entries(this.decisions).filter(([, d]) => !!d)) as Record<string, HistoryDecision>;
    this.patientAppService.resolveHistoryProposal(this.data.proposalId, decisions, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.isSaving = false;
        const filed = Object.values(result.outcomes).filter((o) => o === 'filed').length;
        this.showToastMessage(filed ? `Filed ${filed} ${filed === 1 ? 'item' : 'items'} in the patient's record.` : 'Reviewed; nothing filed.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The decisions could not be saved. Please try again.');
      },
    });
  }

  close(): void {
    this.dialogRef.close({});
  }
}
